import { useEditorState } from '@tiptap/react';
import React, { useCallback, useRef, JSX, useEffect } from 'react';
import { Popper } from '@mui/material';
import { usePopover } from 'hooks/common/usePopover';

import { Toolbar } from '../../../components/ui/Toolbar';
import { Icon } from '../../../components/ui/Icon';
import { ImageBlockWidth } from './ImageBlockWidth';
import { MenuProps } from '../../../components/menus/types';
import { getRenderContainer } from '../../../lib/utils';

export const ImageBlockMenu = ({
  editor,
  appendTo,
}: MenuProps): JSX.Element => {
  const menuRef = useRef<HTMLDivElement>(null);
  const { anchorEl, open, openPopover, closePopover } =
    usePopover<HTMLElement>();

  const getReferenceClientRect = useCallback(() => {
    const renderContainer = getRenderContainer(editor, 'node-imageBlock');
    const rect =
      renderContainer?.getBoundingClientRect() ||
      new DOMRect(-1000, -1000, 0, 0);

    return rect;
  }, [editor]);

  const shouldShow = useCallback(() => {
    const isActive = editor.isActive('imageBlock');
    return isActive;
  }, [editor]);

  const onAlignImageLeft = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setImageBlockAlign('left')
      .run();
  }, [editor]);

  const onAlignImageCenter = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setImageBlockAlign('center')
      .run();
  }, [editor]);

  const onAlignImageRight = useCallback(() => {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .setImageBlockAlign('right')
      .run();
  }, [editor]);

  const onWidthChange = useCallback(
    (value: number) => {
      editor
        .chain()
        .focus(undefined, { scrollIntoView: false })
        .setImageBlockWidth(value)
        .run();
    },
    [editor]
  );

  const { isImageCenter, isImageLeft, isImageRight, width } = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isImageLeft: ctx.editor.isActive('imageBlock', { align: 'left' }),
        isImageCenter: ctx.editor.isActive('imageBlock', { align: 'center' }),
        isImageRight: ctx.editor.isActive('imageBlock', { align: 'right' }),
        width: parseInt(ctx.editor.getAttributes('imageBlock')?.width || 0),
      };
    },
  });

  // Update menu position when image is selected
  const updateMenuPosition = useCallback(() => {
    if (
      !editor.isActive ||
      !editor.view ||
      !shouldShow() ||
      !appendTo?.current
    ) {
      closePopover();
      return;
    }

    const renderContainer = getRenderContainer(editor, 'node-imageBlock');
    if (!renderContainer) {
      closePopover();
      return;
    }

    // Position at the top of the image
    const rect = renderContainer.getBoundingClientRect();

    // Use the appendTo container directly
    openPopover(appendTo.current);

    // Set position data attributes for custom positioning
    if (anchorEl) {
      const popoverElement = anchorEl as HTMLElement;
      popoverElement.dataset.x = `${rect.left + rect.width / 2}`;
      popoverElement.dataset.y = `${rect.top}`;
    }
  }, [editor, shouldShow, closePopover, openPopover, appendTo, anchorEl]);

  // Set up event listeners
  useEffect(() => {
    const onSelectionChange = () => {
      if (shouldShow()) {
        updateMenuPosition();
      } else {
        closePopover();
      }
    };

    editor.on('selectionUpdate', onSelectionChange);
    editor.on('update', onSelectionChange);

    // Run once on mount
    if (shouldShow()) {
      updateMenuPosition();
    }

    return () => {
      editor.off('selectionUpdate', onSelectionChange);
      editor.off('update', onSelectionChange);
    };
  }, [editor, updateMenuPosition, shouldShow, closePopover]);

  return (
    <Popper
      open={open && shouldShow()}
      anchorEl={anchorEl}
      placement='top'
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 10],
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['bottom', 'right', 'left'],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
      ]}
      style={{
        zIndex: 9999,
      }}
      container={appendTo?.current}
    >
      <Toolbar.Wrapper shouldShowContent={shouldShow()} ref={menuRef}>
        <Toolbar.Button
          tooltip='Align image left'
          active={isImageLeft}
          onClick={onAlignImageLeft}
        >
          <Icon name='AlignHorizontalDistributeStart' />
        </Toolbar.Button>
        <Toolbar.Button
          tooltip='Align image center'
          active={isImageCenter}
          onClick={onAlignImageCenter}
        >
          <Icon name='AlignHorizontalDistributeCenter' />
        </Toolbar.Button>
        <Toolbar.Button
          tooltip='Align image right'
          active={isImageRight}
          onClick={onAlignImageRight}
        >
          <Icon name='AlignHorizontalDistributeEnd' />
        </Toolbar.Button>
        <Toolbar.Divider />
        <ImageBlockWidth onChange={onWidthChange} value={width} />
      </Toolbar.Wrapper>
    </Popper>
  );
};

export default ImageBlockMenu;
